import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FiSave, FiUpload, FiClock, FiCalendar } from 'react-icons/fi';
import {
  Employee, listEmployees, listTimesheets, saveTimesheet, savePunch, rollupPunches, extractTimesheet,
} from './payrollApi';
import { loadTable, getTableData } from '../db-viewer/data/dataAccess';
import { setFormDirty } from '../../lib/unsavedChanges';

const REG_HOURS_CAP = 80; // regular hours per period before overtime (matches punch roll-up)

// Duration in hours for a scheduled shift (end − start − break; handles overnight).
function schedShiftHours(s: { start_time?: any; end_time?: any; break_minutes?: any }): number {
  const t = (v: any) => (v ? String(v).slice(0, 5) : '');
  const st = t(s.start_time), en = t(s.end_time);
  if (!st || !en) return 0;
  const [sh, sm] = st.split(':').map(Number);
  const [eh, em] = en.split(':').map(Number);
  let m = (eh * 60 + em) - (sh * 60 + sm);
  if (m < 0) m += 24 * 60;
  m -= Number(s.break_minutes) || 0;
  return Math.max(0, m / 60);
}

function defaultPeriod() {
  // Default to a recent bi-weekly window ending today.
  const end = new Date();
  const start = new Date(end.getTime() - 13 * 86400000);
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
}

export default function TimesheetsTab() {
  const dp = defaultPeriod();
  const [periodStart, setPeriodStart] = useState(dp.start);
  const [periodEnd, setPeriodEnd] = useState(dp.end);
  const [emps, setEmps] = useState<Employee[]>([]);
  const [hours, setHours] = useState<Record<number, { reg: string; ot: string }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [es, ts] = await Promise.all([listEmployees(), listTimesheets(periodStart, periodEnd)]);
      const active = es.filter((e) => e.status === 'active');
      setEmps(active);
      const h: Record<number, { reg: string; ot: string }> = {};
      for (const e of active) h[e.pay_employee_id] = { reg: '0', ot: '0' };
      for (const t of ts) if (h[t.pay_employee_id]) h[t.pay_employee_id] = { reg: String(t.regular_hours || 0), ot: String(t.overtime_hours || 0) };
      setHours(h);
    } catch (e: any) { setError(e.message || 'Could not load'); }
    finally { setLoading(false); }
  }, [periodStart, periodEnd]);
  useEffect(() => { load(); }, [load]);

  const setH = (id: number, k: 'reg' | 'ot', v: string) => { setHours((p) => ({ ...p, [id]: { ...p[id], [k]: v } })); setFormDirty(true); };

  const saveAll = async () => {
    setBusy('save'); setError(''); setInfo('');
    try {
      let n = 0;
      for (const e of emps) {
        const h = hours[e.pay_employee_id];
        if (!h) continue;
        if (Number(h.reg) === 0 && Number(h.ot) === 0) continue;
        await saveTimesheet({ pay_employee_id: e.pay_employee_id, period_start: periodStart, period_end: periodEnd, regular_hours: Number(h.reg), overtime_hours: Number(h.ot) });
        n++;
      }
      setFormDirty(false); setInfo(`Saved ${n} timesheet${n === 1 ? '' : 's'} for ${periodStart} → ${periodEnd}.`);
    } catch (e: any) { setError(e.message || 'Could not save'); }
    finally { setBusy(''); }
  };

  // PDF / image hours extraction → match names to employees → fill inputs.
  const onFile = async (file: File) => {
    setBusy('extract'); setError(''); setInfo('');
    try {
      const dataUrl: string = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = rej; r.readAsDataURL(file);
      });
      const base64 = dataUrl.split(',')[1];
      const { rows } = await extractTimesheet(base64, file.type || 'application/pdf');
      let matched = 0;
      const next = { ...hours };
      for (const row of rows) {
        const name = row.employee_name.toLowerCase().trim();
        const e = emps.find((x) => `${x.first_name} ${x.last_name}`.toLowerCase().trim() === name)
          || emps.find((x) => name.includes(x.last_name.toLowerCase()) && name.includes(x.first_name.toLowerCase()));
        if (e) { next[e.pay_employee_id] = { reg: String(row.regular_hours || 0), ot: String(row.overtime_hours || 0) }; matched++; }
      }
      setHours(next); setFormDirty(true);
      setInfo(`Extracted ${rows.length} row${rows.length === 1 ? '' : 's'}; matched ${matched} to employees. Review the hours below, then Save.`);
    } catch (e: any) { setError(e.message || 'Extraction failed'); }
    finally { setBusy(''); if (fileRef.current) fileRef.current.value = ''; }
  };

  // Pull scheduled hours from the Scheduling pillar into this period's timesheet.
  // Matches each payroll employee to the scheduling roster by EMAIL (name fallback),
  // sums their sched_shift hours in the period, splits regular/overtime (80h cap).
  const importFromSchedule = async () => {
    setBusy('sched'); setError(''); setInfo('');
    try {
      const [shifts, roster] = await Promise.all([
        loadTable('sched_shift').catch(() => getTableData('sched_shift')),
        loadTable('user_business_employee').catch(() => getTableData('user_business_employee')),
      ]);
      const inPeriod = (shifts as any[]).filter((s) => s.is_deleted != 1 && s.status !== 'cancelled' &&
        (s.shift_date || '').slice(0, 10) >= periodStart && (s.shift_date || '').slice(0, 10) <= periodEnd);
      const hoursByRoster = new Map<number, number>();
      for (const s of inPeriod) {
        if (s.employee_id == null) continue;
        const id = Number(s.employee_id);
        hoursByRoster.set(id, (hoursByRoster.get(id) || 0) + schedShiftHours(s));
      }
      const next = { ...hours };
      let matched = 0, totalH = 0;
      for (const e of emps) {
        const email = String((e as any).email || '').toLowerCase().trim();
        const fullname = `${e.first_name} ${e.last_name}`.toLowerCase().trim();
        const rosterMatches = (roster as any[]).filter((r) => r.is_deleted != 1 &&
          ((email && String(r.email || '').toLowerCase().trim() === email) ||
           String(r.emp_name || '').toLowerCase().trim() === fullname));
        let total = 0;
        for (const r of rosterMatches) total += hoursByRoster.get(Number(r.employee_id)) || 0;
        if (total > 0) {
          const reg = Math.min(total, REG_HOURS_CAP), ot = Math.max(0, total - REG_HOURS_CAP);
          next[e.pay_employee_id] = { reg: String(Math.round(reg * 100) / 100), ot: String(Math.round(ot * 100) / 100) };
          matched++; totalH += total;
        }
      }
      setHours(next); setFormDirty(true);
      setInfo(matched === 0
        ? 'No scheduled hours found for these employees in this period (matched by email/name). Check the schedule and employee emails.'
        : `Pulled ${Math.round(totalH * 100) / 100}h from the schedule into ${matched} employee${matched === 1 ? '' : 's'} (regular up to ${REG_HOURS_CAP}h, overtime beyond). Review the hours, then Save.`);
    } catch (e: any) { setError(e.message || 'Could not import from schedule'); }
    finally { setBusy(''); }
  };

  // Quick punch + rollup for one employee.
  const [punchEmp, setPunchEmp] = useState<number | ''>('');
  const [punchIn, setPunchIn] = useState('');
  const [punchOut, setPunchOut] = useState('');
  const [punchBreak, setPunchBreak] = useState('0');
  const addPunch = async () => {
    if (!punchEmp || !punchIn) { setError('Pick an employee and a punch-in time.'); return; }
    setBusy('punch'); setError(''); setInfo('');
    try {
      await savePunch({ pay_employee_id: punchEmp, punch_in: punchIn, punch_out: punchOut || null, break_minutes: Number(punchBreak || 0) });
      setInfo('Punch recorded. Use "Roll up punches" to total them into this period\'s timesheet.');
      setPunchIn(''); setPunchOut(''); setPunchBreak('0');
    } catch (e: any) { setError(e.message || 'Could not save punch'); }
    finally { setBusy(''); }
  };
  const doRollup = async () => {
    if (!punchEmp) { setError('Pick an employee to roll up.'); return; }
    setBusy('rollup'); setError('');
    try { await rollupPunches(Number(punchEmp), periodStart, periodEnd, 80); await load(); setInfo('Punches rolled into the timesheet (regular up to 80h, overtime beyond).'); }
    catch (e: any) { setError(e.message || 'Could not roll up'); }
    finally { setBusy(''); }
  };

  return (
    <div>
      <div className="pay-toolbar">
        <div className="pay-field" style={{ maxWidth: 160 }}><label>Period start</label><input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} /></div>
        <div className="pay-field" style={{ maxWidth: 160 }}><label>Period end</label><input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} /></div>
        <span className="spacer" />
        <input ref={fileRef} type="file" accept="application/pdf,image/*" style={{ display: 'none' }} onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
        <button className="pay-btn ghost" onClick={importFromSchedule} disabled={!!busy}><FiCalendar /> {busy === 'sched' ? 'Importing…' : 'Import from schedule'}</button>
        <button className="pay-btn ghost" onClick={() => fileRef.current?.click()} disabled={!!busy}><FiUpload /> {busy === 'extract' ? 'Reading…' : 'Import hours (PDF/image)'}</button>
        <button className="pay-btn" onClick={saveAll} disabled={!!busy}><FiSave /> {busy === 'save' ? 'Saving…' : 'Save hours'}</button>
      </div>
      {error && <div className="pay-error">{error}</div>}
      {info && <div className="pay-note" style={{ marginBottom: 'var(--spacing-md)' }}>{info}</div>}

      <div className="pay-card" style={{ padding: 0, overflowX: 'auto', marginBottom: 'var(--spacing-lg)' }}>
        {loading ? <div className="pay-empty">Loading…</div>
          : emps.length === 0 ? <div className="pay-empty">No active employees.</div>
          : (
          <table className="pay-table">
            <thead><tr><th>Employee</th><th>Type</th><th className="num">Regular hours</th><th className="num">Overtime hours</th></tr></thead>
            <tbody>
              {emps.map((e) => (
                <tr key={e.pay_employee_id}>
                  <td>{e.first_name} {e.last_name}</td>
                  <td>{e.wage_type === 'salary' ? 'Salary' : 'Hourly'}</td>
                  <td className="num"><input style={{ width: 90, textAlign: 'right' }} type="number" step="0.25" value={hours[e.pay_employee_id]?.reg ?? '0'} onChange={(ev) => setH(e.pay_employee_id, 'reg', ev.target.value)} /></td>
                  <td className="num"><input style={{ width: 90, textAlign: 'right' }} type="number" step="0.25" value={hours[e.pay_employee_id]?.ot ?? '0'} onChange={(ev) => setH(e.pay_employee_id, 'ot', ev.target.value)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <h3>Punch clock</h3>
      <div className="pay-card">
        <div className="pay-grid">
          <div className="pay-field"><label>Employee</label>
            <select value={punchEmp} onChange={(e) => setPunchEmp(e.target.value ? Number(e.target.value) : '')}>
              <option value="">— pick —</option>
              {emps.map((e) => <option key={e.pay_employee_id} value={e.pay_employee_id}>{e.first_name} {e.last_name}</option>)}
            </select>
          </div>
          <div className="pay-field"><label>Punch in</label><input type="datetime-local" value={punchIn} onChange={(e) => setPunchIn(e.target.value)} /></div>
          <div className="pay-field"><label>Punch out</label><input type="datetime-local" value={punchOut} onChange={(e) => setPunchOut(e.target.value)} /></div>
          <div className="pay-field"><label>Break (min)</label><input type="number" value={punchBreak} onChange={(e) => setPunchBreak(e.target.value)} /></div>
        </div>
        <div className="pay-toolbar" style={{ marginTop: 'var(--spacing-md)', marginBottom: 0 }}>
          <button className="pay-btn ghost" onClick={addPunch} disabled={!!busy}><FiClock /> Record punch</button>
          <button className="pay-btn ghost" onClick={doRollup} disabled={!!busy}>Roll up punches → timesheet</button>
          <span style={{ color: 'var(--secondary)', fontSize: 'var(--font-size-xs)' }}>Rolls all punches in the selected period into the employee's timesheet.</span>
        </div>
      </div>
    </div>
  );
}
